import { useEffect, useMemo, useState } from "react";
import { Download, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const toCsv = (rows, cols) =>
  [cols.map((c) => c.label).join(","), ...rows.map((r) => cols.map((c) => `"${String(c.get(r)).replace(/"/g, '""')}"`).join(","))].join("\n");

const downloadCsv = (name, rows, cols) => {
  const url = URL.createObjectURL(new Blob([toCsv(rows, cols)], { type: "text/csv;charset=utf-8" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
};

const DataTable = ({ rows, cols, testId, empty }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={testId}>
    <table className="w-full min-w-[720px] text-sm">
      <thead className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
        <tr>{cols.map((c) => <th key={c.label} className={`px-4 py-3 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length ? rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50" data-testid={`${testId}-row-${i}`}>
            {cols.map((c) => <td key={c.label} className={`px-4 py-3 ${c.right ? "text-right font-mono text-xs" : "text-slate-700"}`}>{c.render ? c.render(r, i) : c.get(r)}</td>)}
          </tr>
        )) : <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);

export const SalesCustomers = () => {
  const { t, locale } = useLang();
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  useEffect(() => {
    api.get("/owner/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    api.get("/owner/sales").then(({ data }) => setSales(data)).catch(() => {});
  }, []);
  const usd = (v) => new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }).format(v);
  const date = (iso) => new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });

  const custCols = useMemo(() => [
    { label: "#", get: (r) => r.rank, render: (r, i) => (i < 3 ? <span className="inline-flex items-center gap-1 font-mono text-xs font-bold"><Crown className="h-4 w-4 text-[#FFB800]" />{i + 1}</span> : i + 1) },
    { label: t("owner.colCustomer"), get: (r) => r.email, render: (r) => <span><span className="block font-semibold text-slate-900">{r.name}</span><span className="text-xs text-slate-500">{r.email}</span></span> },
    { label: t("owner.colSpent"), get: (r) => r.spent, render: (r) => usd(r.spent), right: true },
    { label: t("owner.colOrders"), get: (r) => r.orders, right: true },
    { label: t("owner.colCredits"), get: (r) => r.credits_bought, right: true },
    { label: t("owner.colFiles"), get: (r) => r.files, right: true },
    { label: t("owner.colLast"), get: (r) => r.last_purchase, render: (r) => date(r.last_purchase), right: true },
  ], [t, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const saleCols = useMemo(() => [
    { label: t("owner.colDate"), get: (r) => r.created_at, render: (r) => date(r.created_at) },
    { label: t("owner.colCustomer"), get: (r) => r.email },
    { label: t("owner.colPacks"), get: (r) => r.packs, right: true },
    { label: t("owner.colCredits"), get: (r) => r.credits, right: true },
    { label: t("owner.colAmount"), get: (r) => r.amount, render: (r) => usd(r.amount), right: true },
  ], [t, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="mt-10" data-testid="owner-sales-customers">
      <Tabs defaultValue="customers">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="customers" data-testid="owner-tab-customers">{t("owner.topCustomers")}</TabsTrigger>
            <TabsTrigger value="sales" data-testid="owner-tab-sales">{t("owner.allSales")}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="customers" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" className="rounded-full" data-testid="owner-export-customers" onClick={() => downloadCsv("customers.csv", customers, [
              { label: "name", get: (r) => r.name }, { label: "email", get: (r) => r.email }, { label: "total_spent_usd", get: (r) => r.spent },
              { label: "orders", get: (r) => r.orders }, { label: "credits_bought", get: (r) => r.credits_bought }, { label: "credits_left", get: (r) => r.credits_left },
              { label: "files_processed", get: (r) => r.files }, { label: "first_purchase", get: (r) => r.first_purchase }, { label: "last_purchase", get: (r) => r.last_purchase },
            ])}>
              <Download className="mr-2 h-4 w-4" />{t("owner.exportCsv")}
            </Button>
          </div>
          <DataTable rows={customers} cols={custCols} testId="owner-customers-table" empty={t("owner.noSales")} />
        </TabsContent>
        <TabsContent value="sales" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" className="rounded-full" data-testid="owner-export-sales" onClick={() => downloadCsv("sales.csv", sales, saleCols)}>
              <Download className="mr-2 h-4 w-4" />{t("owner.exportCsv")}
            </Button>
          </div>
          <DataTable rows={sales} cols={saleCols} testId="owner-sales-table" empty={t("owner.noSales")} />
        </TabsContent>
      </Tabs>
    </section>
  );
};
